import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { CustomerResponseTypes } from "../common/response.common";
import type { CompleteHook, CompleteParam, SelectHook, SelectParam } from "../common/types.common";
import {
    asRemoveHook,
    asUpdateHook,
    buildCompleteParam,
    buildSelectParam,
    get,
    post,
    postEmpty,
} from "./common";

export const customer = {
    record(data: typeof Requests.Customer.Record.Input) {
        return post<typeof data, CustomerResponseTypes.Record>(`/customer/record`, data);
    },
    create(data: typeof Requests.Customer.Create.Input) {
        if (import.meta.env.MODE === "production") throw new Error("INVALID ENDPOINT");
        return post<typeof data>(`/customer/create`, data);
    },
    obtain(id: string) {
        return get<CustomerResponseTypes.Obtain>(`/customer/obtain/${id}`);
    },
    coords(data: typeof Requests.Customer.Coords.Input) {
        return post<typeof data, CustomerResponseTypes.Coords>(`/customer/coords`, data);
    },
    delete(id: string) {
        return postEmpty(`/customer/delete/${id}`);
    },
    formHooks: {
        select: (async (param?: SelectParam) => {
            const req = get<CustomerResponseTypes.Select>(
                `/customer/select` + buildSelectParam(param),
            );

            return (await req.execute()) ?? [];
        }) satisfies SelectHook,
        completeArea: (async (param?: CompleteParam) => {
            const req = get<CustomerResponseTypes.CompleteArea>(
                `/customer/complete-area` + buildCompleteParam(param),
            );

            return (await req.execute()) ?? [];
        }) satisfies CompleteHook,
        update(id: string | undefined, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Customer.Update.Input) => {
                if (id === undefined) return false;
                return await asUpdateHook(
                    post<typeof data>(`/customer/update/${id}`, data),
                    isLoading,
                );
            };
        },
        remove(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Customer.Remove.Input) => {
                return await asRemoveHook(
                    post<typeof data>(`/customer/remove/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
